import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DateConverter from "@heartinz/date_and_time_processor";
import CurrencyConverter from "@heartinz/currency_subunit_converter";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import { useAuth } from "../../../context/AuthProvider";
import AxiosInstance from "../../../utils/AxiosInstance";
import Messages from "../../../constants/Messages";
import handleError from "../../../constants/handleError";

import AddExpense from "./AddExpense";
import DeleteModal from "../../../constants/DeleteModal";
import GetServerLiveTime from "../../../constants/GetServerLiveTime";
import { usePermission } from "../../../context/PermissionContext";
import useKeyboardTableNavigation from "../../../constants/useKeyboardTableNavigation";
import Table from "../../../constants/Table";
import { AddIcon, CalendarIcon, DeleteIcon, DownloadIcon, SpreadSheetIcon } from "../../../constants/Icons";

const Expense = ({ isActive }) => {
  const [expenseDate, setExpenseDate] = useState("");
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [isExpenseData, setExpenseData] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);
  const [totalItem, setTotalItems] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isBtnLoading, setBtnLoading] = useState(false);
  const [isPreLoading, setPreLoading] = useState(false);
  const { permission, hasPermission, globelPopupOpen } = usePermission();
  const containerRef = useRef(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [isDeleteData, setDeleteData] = useState();
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();
  const { isUser } = useAuth();

  const serverTime = GetServerLiveTime();

  const iso = serverTime.toISOString();
  const currentDate = DateConverter.convertToTimeZone(iso, isUser?.timezone);
  const getBgColor = (status) => { 
    if (!status || typeof status !== "string") return "bg-label-secondary";
    switch (status.toLowerCase()) {
      case "cash":
        return "bg-label-success";
      case "upi":
        return "bg-label-moon";
      case "card":
        return "bg-label-flower";
      default:
        return "bg-label-secondary";
    }
  };

  const handleOpenModal = () => {
    if (hasPermission("EXPENSE", "EXPENSE:add") || permission === undefined) {
      setModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleExpenseDate = ([selectedDate]) => {
    const isoDateTime = selectedDate.toISOString();
    const currentDate = DateConverter.convertToTimeZone(
      isoDateTime,
      isUser?.timezone,
    );
    setExpenseDate(currentDate);
    handleExpenseList(isoDateTime);
  };

  const openDeleteModal = (cItem) => {
    if (
      hasPermission("EXPENSE", "EXPENSE:delete") ||
      permission === undefined
    ) {
      setDeleteData(cItem);
      setDeleteModalOpen(true);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleExpenseList = async (expensedate, loadMore = false) => {
    const perPage = 10;
    if (!loadMore) {
      setPreLoading(true);
    } else {
      setIsLoadingMore(true);
    }

    const currentCount = isExpenseData.length;

    const limit = loadMore ? currentCount : perPage;
    const page = loadMore ? 2 : 1;

    try {
      const response = await AxiosInstance.post(`module/expense/list-expense`, {
        expensedate: DateConverter.convertToTimeZone(
          expensedate,
          isUser?.timezone,
        ),
        pageno: page,
        limit: limit,
      });

      if (!loadMore) {
        setExpenseData(response.data.results);
      } else {
        setExpenseData((prev) => [...prev, ...response.data.results]);
      }

      if (!loadMore) {
        setTotalItems(response.data.totalitems || 0);
        setTotalExpense(response.data.totalexpense || 0);
      } else {
        setTotalExpense((prev) => prev + response.data.totalexpense);
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setPreLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    handleExpenseList(expenseDate, true);
  };

  const handleDelete = async () => {
    setBtnLoading(true);
    try {
      if (!checkNetwork()) return;
      const data = { expenseid: isDeleteData._id };
      const response = await AxiosInstance.delete(
        `module/expense/delete-expense`,
        {
          data: data,
        },
      );
      showAlert("success", Messages.SUCCESS.EXPENSE_DELETED);
      setExpenseData((prev) =>
        prev.filter((item) => item._id !== isDeleteData._id),
      );
      setTotalItems((prev) => prev - 1);
      setTotalExpense((prevTotal) => prevTotal - response.data.results.amount);
      setDeleteModalOpen(false);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setBtnLoading(false);
    }
  };

  useKeyboardTableNavigation({
    isActive,
    containerRef,
    modals: {
      isDeleteModalOpen,
      isModalOpen,
      globelPopupOpen,
    },
    onEnter: openDeleteModal,
  });

  useEffect(() => {
    const isoDateTime = serverTime.toISOString();
    const india = DateConverter.convertToTimeZone(
      isoDateTime,
      isUser?.timezone,
    );
    setExpenseDate(india);
    handleExpenseList(isoDateTime);
  }, []);

  const isValidDate = (date) => date instanceof Date && !isNaN(date);

  const expenseDateObj = new Date(expenseDate);

  const exportToCSV = () => {
    if (isExpenseData.length === 0) {
      showAlert("warning", "No data available to export");
      return;
    }

    const headers = `S.No,Add On,Title,Description,Payment Mode,Amount\n`;

    const rows = isExpenseData
      .map((item, index) => {
        const date = DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(item.createdAt),
          "dd LLL yyyy",
        ).date;

        const time = DateConverter.ISOToDisplayFormat(
          DateConverter.convertToTimeZone(item.createdAt),
          "hh:mm a",
        ).time;
        const dateTime = `="${date}\u2009\u2009${time}"`;
        const clean = (val) => (val ?? "").toString().replace(/[\r\n;]/g, " ");

        return [
          index + 1,
          clean(dateTime),
          clean(item.title),
          clean(item.description),
          clean(item.paymode),
          clean(CurrencyConverter.toCurrency(item.amount || 0, "INR")),
        ].join(",");
      })
      .join("\n");

    const csvContent = headers + rows;

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Expenses_${
        expenseDate &&
        DateConverter.ISOToDisplayFormat(expenseDate, isUser.timezone).date
      }.csv`,
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="page-content">
            <div className="page-breadcrumb d-flex align-items-center">
              <div className="breadcrumb-title pe-3 py-2">Expense</div>{" "}
              {/* {totalItem} */}
              <div className="ms-auto">
                <div className="dropdown">
                  <button
                    className="btn btn-export"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <DownloadIcon/> Export
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <Link className="dropdown-item" onClick={exportToCSV}>
                        <SpreadSheetIcon/>{" "}
                        <span className="icon-text">Excel (CSV)</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12">
                <div className="card radius-10 mb-0 mt-3">
                  <div className="card-widget-separator-wrapper">
                    <div className="card-header justify-content-between align-items-center px-3 py-3 border-0">
                      <div className="d-md-flex align-items-center justify-content-between">
                        <div className="d-md-flex align-items-center gap-2">
                          <div className="input-group w-200">
                            <span className="input-group-text">
                              <CalendarIcon/>
                            </span>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                open={openDatePicker}
                                onOpen={() => setOpenDatePicker(true)}
                                onClose={() => setOpenDatePicker(false)}
                                disableOpenPicker
                                value={dayjs(expenseDate || currentDate)}
                                maxDate={dayjs(currentDate)}
                                format="DD MMM YYYY"
                                onChange={(newValue) => {
                                  if (!newValue) return;
                                  handleExpenseDate([newValue.toDate()]);
                                  setOpenDatePicker(false);
                                }}
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    size: "small",
                                    className: "form-control cursor-pointer",
                                    onClick: () => setOpenDatePicker(true),
                                    inputProps: { readOnly: true },
                                    onKeyDown: (e) => e.preventDefault(),
                                    sx: {
                                      "& .MuiInputBase-input": {
                                        cursor: "pointer",
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>

                          <div className="text-center py-1 ms-2">
                            <div className="chip border-0 text-primary bg-label-expense mb-0">
                              Total : ₹
                              {CurrencyConverter.toCurrency(
                                totalExpense || 0,
                                "INR",
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-center py-1 ms-2">
                          <div className="border-0 fw-500 text-dark  mb-0">
                            <h6 class="mb-0 text-expense text-center">
                              {isValidDate(expenseDateObj) &&
                              DateConverter.ISOToDisplayFormat(
                                DateConverter.convertToTimeZone(
                                  currentDate,
                                  isUser.timezone,
                                ),
                              ).date ===
                                DateConverter.ISOToDisplayFormat(
                                  DateConverter.convertToTimeZone(
                                    expenseDate,
                                    isUser.timezone,
                                  ),
                                ).date
                                ? `Expense - Today`
                                : isValidDate(expenseDateObj)
                                  ? `Expense - ${
                                      DateConverter.ISOToDisplayFormat(
                                        DateConverter.toISO(
                                          formatDate(expenseDateObj),
                                          "yyyy-MM-dd",
                                          isUser.timezone,
                                        ),
                                      ).date
                                    }`
                                  : `Expense - Invalid Date`}
                            </h6>
                          </div>
                        </div>
                        <div className="text-center py-1 ms-2">
                          <div className="ms-auto">
                            <button
                              type="button"
                              onClick={handleOpenModal}
                              className="btn btn-primary"
                            >
                              <AddIcon/> Add Expense
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-body px-0 pt-0">
                      <div ref={containerRef}>
                        <Table
                          loading={isPreLoading}
                          data={isExpenseData}
                          onRowClick={(item) => openDeleteModal(item)}
                          columns={[
                            {
                              label: "S.No",
                              render: (_, index) => <>{totalItem - index}</>,
                              thClass: "w-5",
                            },
                            {
                              label: "Added On",
                              thClass: "w-5",
                              render: (item) => (
                                <>
                                  {
                                    DateConverter.ISOToDisplayFormat(
                                      DateConverter.convertToTimeZone(
                                        item.createdAt,
                                        isUser?.timezone,
                                      ),
                                    ).date
                                  }
                                  , &nbsp;
                                  {
                                    DateConverter.ISOToDisplayFormat(
                                      DateConverter.convertToTimeZone(
                                        item.createdAt,
                                        isUser?.timezone,
                                      ),
                                    ).time
                                  }
                                </>
                              ),
                            },
                            {
                              label: "Title",
                              render: (item) => (
                                <span className="text-heading fw-medium">
                                  {item.title}
                                </span>
                              ),
                            },
                            {
                              label: "Description",
                              render: (item) => (
                                <div className="d-flex flex-column">
                                  <small className="text-truncate d-none d-sm-block">
                                    {item.description}
                                  </small>
                                </div>
                              ),
                            },
                            {
                              label: "Payment Mode",
                              thClass: "w-6",
                              render: (item) => (
                                <span
                                  className={`badge rounded-pill ${getBgColor(
                                    item.paymode,
                                  )}`}
                                >
                                  {item.paymode}
                                </span>
                              ),
                            },
                            {
                              label: "Amount",
                              thClass: "w-6",
                              render: (item) => (
                                <span className="text-heading fw-medium text-truncate fs-15">
                                  ₹
                                  {CurrencyConverter.toCurrency(
                                    Number(item.amount) || 0,
                                    "INR",
                                  )}
                                </span>
                              ),
                            },
                            {
                              label: "Actions",
                              thClass: "w-6",
                              render: (item) => (
                                <div className="order-actions opacity-0 opacity-md-100">
                                  {item.sourcemode === "EXPENSE" && (
                                    <button
                                      type="button"
                                      className="btn btn-label-danger shadow-none me-2"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openDeleteModal(item);
                                      }}
                                    >
                                      <DeleteIcon/>
                                    </button>
                                  )}
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                      {totalItem > 10 && isExpenseData.length < totalItem && (
                        <div className="text-center mt-3">
                          <button
                            onClick={handleLoadMore}
                            disabled={isLoadingMore}
                            className="btn btn-primary"
                          >
                            {isLoadingMore ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-3"></span>
                                Loading...
                              </>
                            ) : (
                              "Load More"
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isModalOpen && (
            <AddExpense
              isActive={isActive}
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              expenseDate1={expenseDate}
              isExpenseData={isExpenseData}
              setExpenseData={setExpenseData}
              setExpenseDate={setExpenseDate}
              setTotalExpense={setTotalExpense}
              setTotalItems={setTotalItems}
              handleExpenseList={handleExpenseDate}
              listExpense={handleExpenseList}
              currentDate={currentDate}
            />
          )}
          {isDeleteModalOpen && (
            <DeleteModal
              onClose={() => setDeleteModalOpen(false)}
              onConfirm={handleDelete}
              isLable={" Expense " + isDeleteData?.title}
              isBtnLoading={isBtnLoading}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Expense;
